import { Input } from '@/components/ui/input';
import { useState } from 'react';
import {
  X,
  CheckCircle2,
  AlertCircle,
  Coins,
  Sparkles,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useBetSlip, useUserBets } from '../hooks';
import { useWallet } from '@/features/wallet/hooks/use-wallet';
import { Button } from '@/components/ui/button';

export function PredictionSlip() {
  const { item, isOpen, closeSlip, clearSlip, updateStake } = useBetSlip();
  const { balance, placeBet: deductFunds, claimFaucet } = useWallet();
  const { placeBet: recordBet } = useUserBets();

  const [minimized, setMinimized] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !item) return null;

  const stake = item.stake || 100;
  const potentialPayout = Math.round(stake * item.odds);
  const potentialProfit = potentialPayout - stake;
  const hasEnoughBalance = balance >= stake;

  const quickStakes = [50, 100, 250, 500, 1000];

  const handleConfirm = async () => {
    if (!hasEnoughBalance) {
      setErrorMsg('Insufficient balance. Claim free coins below!');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      // 1. Deduct funds from game wallet
      const deducted = deductFunds(stake, item.marketTitle);
      if (!deducted) {
        setErrorMsg('Failed to deduct funds. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // 2. Record active prediction
      await recordBet({
        marketId: item.marketId,
        marketTitle: item.marketTitle,
        category: 'crypto', // generic fallback or derived
        optionId: item.optionId,
        optionLabel: item.optionLabel,
        odds: item.odds,
        stake,
      });

      setSuccessMsg(
        `Prediction placed for ${stake} PRED! Potential payout: ${potentialPayout} PRED.`,
      );
      setTimeout(() => {
        setSuccessMsg(null);
        clearSlip();
      }, 2500);
    } catch {
      setErrorMsg('Could not place prediction. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="region"
      aria-label="Prediction slip"
      className="fixed bottom-4 right-4 z-40 max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-sm overflow-y-auto rounded-xl border border-primary/40 bg-popover p-5 shadow-lg"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border/80 pb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/20 text-primary">
            <Coins className="h-4 w-4" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-foreground">Prediction Slip</h4>
            <p className="text-xs text-muted-foreground font-mono">
              Balance:{' '}
              <span className="text-success font-bold">{balance.toLocaleString()} PRED</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMinimized(!minimized)}
            className="flex size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Toggle slip"
          >
            {minimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={closeSlip}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close slip"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!minimized && (
        <div className="mt-3 space-y-3">
          {successMsg ? (
            <div
              role="status"
              className="flex flex-col items-center justify-center py-6 text-center animate-in zoom-in-95"
            >
              <div className="mb-2 rounded-full bg-success/12 p-2 text-success">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h5 className="font-bold text-foreground">Prediction Locked!</h5>
              <p className="mt-1 text-xs text-muted-foreground">{successMsg}</p>
            </div>
          ) : (
            <>
              {/* Selected Market Info */}
              <div className="rounded-xl border border-border bg-secondary/40 p-3">
                <p className="line-clamp-1 text-xs text-muted-foreground">{item.marketTitle}</p>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-sm font-bold text-foreground">{item.optionLabel}</span>
                  <span className="rounded bg-primary/20 px-2 py-0.5 text-xs font-bold font-mono text-success">
                    {item.odds.toFixed(2)}x Odds
                  </span>
                </div>
              </div>

              {/* Stake input */}
              <div>
                <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                  <label htmlFor="slip-stake">Stake amount (PRED)</label>
                  <button
                    type="button"
                    onClick={() => updateStake(balance)}
                    className="min-h-9 text-primary hover:underline font-mono text-sm"
                  >
                    MAX ({balance})
                  </button>
                </div>

                <div className="relative mt-1">
                  <Input
                    id="slip-stake"
                    type="number"
                    min={1}
                    max={balance}
                    value={stake}
                    onChange={(e) => updateStake(Number(e.target.value) || 0)}
                    className="pr-16 font-mono"
                  />
                  <span className="absolute right-3 top-2.5 text-xs font-semibold text-muted-foreground">
                    PRED
                  </span>
                </div>

                {/* Quick stake selector buttons */}
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  {quickStakes.map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => updateStake(val)}
                      aria-pressed={stake === val}
                      className={`min-h-9 min-w-10 flex-1 rounded-md border px-2 py-1 text-sm font-mono font-medium transition-colors ${
                        stake === val
                          ? 'border-primary bg-primary/20 text-primary'
                          : 'border-border bg-secondary/60 hover:bg-secondary text-muted-foreground'
                      }`}
                    >
                      +{val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Return Calculation summary */}
              <div className="rounded-lg bg-secondary/60 p-2.5 text-xs space-y-1 font-mono">
                <div className="flex justify-between text-muted-foreground">
                  <span>Potential Payout:</span>
                  <span className="font-bold text-foreground">
                    {potentialPayout.toLocaleString()} PRED
                  </span>
                </div>
                <div className="flex justify-between text-success font-semibold">
                  <span>Estimated Profit:</span>
                  <span>+{potentialProfit.toLocaleString()} PRED</span>
                </div>
              </div>

              {/* Error warning */}
              {errorMsg && (
                <div
                  role="alert"
                  className="flex items-center gap-1.5 text-sm text-destructive bg-destructive/12 p-2 rounded-lg border border-destructive/20"
                >
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}

              {!hasEnoughBalance && (
                <Button
                  type="button"
                  variant="gold"
                  size="sm"
                  className="w-full gap-1.5"
                  onClick={() => {
                    claimFaucet(1000);
                    setErrorMsg(null);
                  }}
                >
                  <Sparkles className="h-4 w-4" />
                  Claim +1,000 Free Coins Faucet
                </Button>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearSlip}
                  className="w-1/3"
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  disabled={!hasEnoughBalance || stake <= 0}
                  loading={isSubmitting}
                  onClick={handleConfirm}
                  className="flex-1 font-bold"
                >
                  Place prediction
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
